import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { Checkbox } from '@webapp/common/components/checkbox';
import { CustomStylesCtx, useTextStyle } from '@webapp/common/components/custom-styles';
import { ImageSlider } from '@webapp/common/components/image-slider';
import { CssQuestionType } from '@webapp/common/components/survey-custom';
import { CDN_URL } from '@webapp/common/conf';
import { CANTRATE_ID } from '@webapp/common/resources/constants';
import type { ImageTextPosition } from '@webapp/common/resources/entities';
import { QuestionType } from '@webapp/common/resources/survey';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import type { QuestionAnswerActor } from '@webapp/survey/src/entities/answer-machine';

import { Image } from './image';

import css from './select-image.css';

export const SelectImage: VFC<SurveyQuestion> = ({
    question: {
        answersActors: answersActorsRaw,
        params: {
            cantRate,
            cantRateText,
            imageColumns: columns,
            imageTextPosition: textPosition,
            size,
            maxLength: max,
        },
        type,
        answeredCount,
        responseActor,
        id: questionId,
    },
}) => {
    const { value } = useSelector(responseActor, (r) => r.context);
    const [, updateRerenderHack] = useState(1);
    useEffect(() => {
        const sub = () => updateRerenderHack((prev) => (prev + 1) % 10);
        const unsubs = answersActors.map((actor) => {
            return actor.subscribe(sub);
        });
        return () => {
            unsubs.forEach((unsub) => unsub.unsubscribe());
        };
    }, []);
    const answersActors = answersActorsRaw.filter(
        (answersActor) => answersActor.getSnapshot().context.hiddenByAnswersHide !== true,
    );
    const [isModalOpen, setModalOpen] = useState<boolean | number>(false);
    const { itemsGap } = useTextStyle();
    const { selectImage } = useContext(CustomStylesCtx);
    const multiple = type === QuestionType.SELECT_FEW_IMAGE;
    const initialImg = typeof isModalOpen === 'number' ? isModalOpen : 0;
    const slides = useMemo(
        () =>
            answersActors.map((answersActor) => {
                const { file, id, response } = answersActor.getSnapshot().context;
                const value = response.getSnapshot().context.value;
                return {
                    id,
                    img: {
                        url: CDN_URL + file?.path,
                        width: file?.width,
                        height: file?.height,
                    },
                    checked: value as boolean,
                };
            }),
        [answersActors],
    );

    const imagesWrapStyle: CSSProperties = useMemo(() => {
        const layout = Array(columns).fill('1fr').join(' ');
        return {
            width: `${size}%`,
            gridTemplateColumns: layout,
            gap: itemsGap,
        };
    }, [columns, itemsGap, size]);

    const commonAspect: number = useMemo(() => {
        const aspects = [];
        answersActors.forEach((answersActor) => {
            const { height, width } = answersActor.getSnapshot().context.file || { width: 0, height: 0 };
            aspects.push(width / height);
        });

        return aspects.reduce((sum, a) => sum + a, 0) / aspects.length;
    }, [answersActors]);

    const toggleSelect = useCallback(
        (answer: QuestionAnswerActor) =>
            answer.getSnapshot().context.response.send({
                type: 'CHANGE',
                value: !answer.getSnapshot().context.response.getSnapshot().context.value,
                questionId: answer.getSnapshot().context.questionId,
            }),
        [],
    );

    const handleClickImage = useCallback(
        (answer: QuestionAnswerActor) => (e) => {
            e.preventDefault();

            return toggleSelect(answer);
        },
        [toggleSelect],
    );

    const handleSelect = useCallback(
        (id: number, checked: boolean) => {
            const answer = answersActors.find((answersActor) => answersActor.getSnapshot().context.id === id);
            answer.getSnapshot().context.response.send({
                type: 'CHANGE',
                value: checked,
                questionId: answer.getSnapshot().context.questionId,
            });
        },
        [answersActors],
    );

    const handleZoomImg = useCallback(
        (idx: number) => (event: ReactMouseEvent<SVGSVGElement, MouseEvent>) => {
            event.stopPropagation();
            event.preventDefault();
            setModalOpen(idx);
        },
        [],
    );

    const handleCantRate = useCallback(
        (checked: boolean) => {
            responseActor.send({ type: 'CHANGE', value: checked ? CANTRATE_ID : '', questionId });

            const answer = answersActors[0];
            answer.getSnapshot().context.response.send({
                type: 'CHANGE',
                value: checked ? CANTRATE_ID : '',
                questionId,
            });
        },
        [responseActor, questionId],
    );

    const closeSlider = useCallback(() => setModalOpen(false), []);

    return (
        <div className={cn(CssQuestionType.QUESTION, CssQuestionType.IMAGE_SELECT, css.questionWrap)}>
            {isModalOpen !== false &&
                createPortal(
                    <ImageSlider
                        initialImg={initialImg}
                        multiple={multiple}
                        slides={slides}
                        onClose={closeSlider}
                        onSelect={handleSelect}
                    />,
                    document.body,
                )}
            <div
                style={imagesWrapStyle}
                className={cn(css.list, {
                    [css.col1]: columns === 1,
                    [css.col2]: columns === 2,
                    [css.col3]: columns === 3,
                })}
            >
                {answersActors.map((answerActor, idx) => (
                    <Image
                        answer={answerActor}
                        aspect={commonAspect}
                        gap={itemsGap}
                        index={idx}
                        key={answerActor.getSnapshot().context.id}
                        selectImage={selectImage}
                        textPosition={textPosition as ImageTextPosition}
                        type={type}
                        onClick={handleClickImage(answerActor)}
                        onClickZoom={handleZoomImg}
                        disabled={Boolean(max) && answeredCount >= max}
                    />
                ))}
            </div>
            {cantRate && (
                <div className={css.checkboxWrap} style={{ marginTop: '10px' }}>
                    <Checkbox checked={value === CANTRATE_ID} label={cantRateText} onChange={handleCantRate} />
                </div>
            )}
        </div>
    );
};
SelectImage.displayName = 'SelectImage';
